import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import CancelBatches from "../../elements/batches/CancelBatches";
import RenderStatus from "../../styles/styledStatus";
import { TimestampToDateMoment } from "../../../utils/timestampToDate";
import { Alert } from "@material-ui/lab";
import PageSpinner from "../../elements/spinner/PageSpinner";
import Notification from "../../elements/notification/Notification";
import { CopyToClipboard } from "react-copy-to-clipboard/lib/Component";
import StartBatches from "../../elements/batches/StartBatches";
import UpdateBatchesStatus from "../../elements/batches/UpdateBatchesStatus";
import { useTranslation } from "react-i18next";

import {
  StyledCol,
  StyledScrollTable,
  StyledTable,
  StyledTableBody,
  StyledTableHead,
  StyledTableRow
} from "../../styles/styledTable";
import { StyledCopy } from "../../styles/styledCopy";
import { StyledBreakWord } from "../../styles/styledBreakWord";
import { StyledTooltip } from "../../styles/styledTooltip";
import { StyledBatchResults } from "../styledBatches";

const BatchesList = ({ fetchBatches, batches }) => {
  const { t } = useTranslation("batchesPage");

  const [notification, setNotification] = useState({
    visible: false,
    type: "",
    message: ""
  });

  if (!batches) {
    return <PageSpinner
      position="center"
      pt="20"
      pb="20"
    />;
  }

  if (batches.length === 0) {
    return <Alert severity="warning">{t("table.emptyData")}</Alert>;
  }

  const showDetails = (e) => {
    if (e.target.closest(".stop-event")) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <>
      <Notification
        notification={notification}
        setNotification={setNotification}
      />
      <StyledScrollTable className="batches-table_scroll">
        <StyledTable
          width="1545"
          size="14"
          className="batches-table"
        >
          <StyledTableHead
            scroll="auto"
            col="8"
            className="batches-table__head"
          >
            <div>
              {t("table.name")}
            </div>
            <div>
              {t("table.amount")}
            </div>
            <div>
              {t("table.processedAmount")}
            </div>
            <StyledBatchResults>
              <StyledTooltip className="icon-light-help" />
              <p title={t("table.batchResult.all")}>
                {t("table.batchResult.abbreviated.all")}
              </p>
              <p title={t("table.batchResult.createWaiting")}>
                {t("table.batchResult.abbreviated.createWaiting")}
              </p>
              <p title={t("table.batchResult.created")}>
                {t("table.batchResult.abbreviated.created")}
              </p>
              <p title={t("table.batchResult.processed")}>
                {t("table.batchResult.abbreviated.processed")}
              </p>
              <p title={t("table.batchResult.failed")}>
                {t("table.batchResult.abbreviated.failed")}
              </p>
              <p title={t("table.batchResult.pending")}>
                {t("table.batchResult.abbreviated.pending")}
              </p>
            </StyledBatchResults>
            <div>
              {t("table.status")}
            </div>
            <div>
              {t("table.createdAt")}
            </div>
            <div>
              {t("table.startedAt")}
            </div>
            <div>
              {t("table.actions")}
            </div>
          </StyledTableHead>
          <StyledTableBody>
            {batches && batches.map((value, key) => (
              <StyledTableRow
                key={key}
                scroll="auto"
                col="8"
                className="batches-table__row"
                onClick={(e) => {showDetails(e);}}
                cursor="pointer"
                data-action="details"
                as={NavLink}
                to={`/batches/${value.id}`}
              >
                <StyledCol
                  inline
                  data-title={t("table.name")}
                  data-action="details"
                >
                  <CopyToClipboard
                    className="stop-event"
                    text={value.name}
                    onCopy={() => {
                      setNotification({ ...notification, visible: true, type: "success", message: t("table.copied") });
                    }}
                  >
                    <StyledBreakWord as="p">
                      {value.name}
                      <StyledCopy
                        title={t("table.copy")}
                        className="icon-light-copy"
                      />
                    </StyledBreakWord>
                  </CopyToClipboard>
                </StyledCol>
                <StyledCol data-title={t("table.amount")} data-action="details">
                  {Number(value.amount).toFixed(4)}
                </StyledCol>
                <StyledCol data-title={t("table.processedAmount")} data-action="details">
                  {Number(value.processedAmount).toFixed(4)}
                </StyledCol>
                <StyledCol data-title={t("table.batchResult.abbreviated.main")} data-action="details">
                  <StyledBatchResults>
                    <p title={t("table.batchResult.all")}>{value.countOfAll}</p>
                    <p title={t("table.batchResult.createWaiting")}>{value.countOfCreateWaiting}</p>
                    <p title={t("table.batchResult.created")}>{value.countOfCreated}</p>
                    <p title={t("table.batchResult.processed")}>{value.countOfProcessed}</p>
                    <p title={t("table.batchResult.failed")}>{value.countOfFailed}</p>
                    <p title={t("table.batchResult.pending")}>{value.countOfPending}</p>
                  </StyledBatchResults>
                </StyledCol>
                <StyledCol
                  inline
                  data-title={t("table.status")}
                  data-action="details"
                >
                  <RenderStatus minWidth="150" status={value.status.value} />
                </StyledCol>
                <StyledCol data-title={t("table.createdAt")} data-action="details">
                  {TimestampToDateMoment(value.createDate)}
                </StyledCol>
                <StyledCol data-title={t("table.startedAt")} data-action="details">
                  {value.startDate !== 0 ? TimestampToDateMoment(value.startDate) : t("table.missing")}
                </StyledCol>
                <StyledCol data-title={t("table.actions")} className="batches-table__actions">
                  <div>
                    {value.status.value === "NEW" &&
                      <>
                        <StartBatches
                          fetchBatches={fetchBatches}
                          batches={value}
                        />
                        <CancelBatches
                          t={t}
                          fetchBatches={fetchBatches}
                          batches={value}
                          notification={notification}
                          setNotification={setNotification}
                        />
                      </>
                    }
                    {value.status.value === "PENDING" &&
                      <UpdateBatchesStatus
                        fetchBatches={fetchBatches}
                        batches={value}
                      />}

                    {value.status.value === "WAITING" &&
                      <Alert severity="warning">{t("table.creatingInvoices")}</Alert>
                    }
                  </div>
                </StyledCol>
              </StyledTableRow>
            ))}
          </StyledTableBody>
        </StyledTable>
      </StyledScrollTable>
    </>
  );
};

export default BatchesList;